import { useCallback, useMemo, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { WizardCtx } from "../../register/WizardContext";
import StepApplicant from "../../register/steps/StepApplicant";
import StepCandidate from "../../register/steps/StepCandidate";
import StepEducation from "../../register/steps/StepEducation";
import StepFamily from "../../register/steps/StepFamily";
import StepContact from "../../register/steps/StepContact";
import StepPreferences from "../../register/steps/StepPreferences";
import { fromRecords, buildPayload } from "../../../lib/formData";
import { validateStep } from "../../../lib/validation";
import { supabase } from "../../../lib/supabase";

const SECTIONS = [
  ["Applicant details", StepApplicant], ["Bride / Groom details", StepCandidate], ["Education & occupation", StepEducation],
  ["Family details", StepFamily], ["Contact details", StepContact], ["Partner preferences & about", StepPreferences],
];

export function EditProfileForm({ rec, onSaved }) {
  const [data, setData] = useState(() => fromRecords(rec));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = useCallback((section, name, value) => {
    setData((p) => ({ ...p, [section]: { ...p[section], [name]: value } }));
    setErrors((e) => { const k = `${section}.${name}`; if (!e[k]) return e; const n = { ...e }; delete n[k]; return n; });
  }, []);
  const ctx = useMemo(() => ({ data, set, errors, userId: null, goTo: () => {}, mode: "admin" }), [data, set, errors]);

  const save = async () => {
    const errs = Object.assign({}, ...[1, 2, 3, 4, 5, 6].map((s) => validateStep(s, data, { admin: true })));
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the highlighted fields.");
      setTimeout(() => document.querySelector('[data-error="true"]')?.scrollIntoView({ block: "center", behavior: "smooth" }), 60);
      return;
    }
    setBusy(true);
    const p = buildPayload(data);
    const id = rec.profile.id;
    const results = await Promise.all([
      supabase.from("profiles").update(p.candidate).eq("id", id),
      supabase.from("family_details").upsert({ ...p.family, profile_id: id }, { onConflict: "profile_id" }),
      supabase.from("profile_private_details").upsert({ ...p.private, profile_id: id }, { onConflict: "profile_id" }),
      supabase.from("partner_preferences").upsert({ ...p.preferences, profile_id: id }, { onConflict: "profile_id" }),
    ]);
    setBusy(false);
    const err = results.find((r) => r.error)?.error;
    if (err) return toast.error(err.message);
    toast.success("Profile updated");
    onSaved();
  };

  return (
    <WizardCtx.Provider value={ctx}>
      <div className="space-y-6" data-testid="edit-profile-form">
        {SECTIONS.map(([title, C]) => (
          <section key={title} className="rmm-card p-5 sm:p-6">
            <h3 className="mb-4 font-display text-2xl font-semibold text-[#7A0016]">{title}</h3>
            <C />
          </section>
        ))}
        <div className="sticky bottom-0 z-20 flex justify-end rounded-xl border border-[#D4AF37]/30 bg-[#FAFAF7]/95 p-3 backdrop-blur">
          <button type="button" onClick={save} disabled={busy} data-testid="save-profile-button" className="inline-flex items-center gap-2 rounded-full bg-[#7A0016] px-6 py-2 text-sm font-semibold text-[#F5E6BA] hover:bg-[#4A000E]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
          </button>
        </div>
      </div>
    </WizardCtx.Provider>
  );
}
